/* eslint-disable react-native/no-inline-styles */
/* Inline styles needed (100vh, 100vw) so that map can scale correctly */

import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import config from 'config/config';
import useBottomMenuHeight from 'hooks/useBottomMenuHeight';
import useIsDesktop from 'hooks/useIsDesktop';
import React, { CSSProperties, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dimensions } from 'react-native';
import { Region } from 'react-native-maps';
import { useMapStore } from 'stores/mapStore';
import mapStyles from 'styles/mapStyle.json';
import { WebAndMobileRegion } from '../../../types/MapTypes';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const locationIcon: any = {
  url: '/images/location.svg',
  scaledSize: { height: 25, width: 25 },
  origin: { x: 0, y: 0 },
};

type WebViewGoogleMap = {
  children: React.ReactNode;
  showBeforeMapLoaded: boolean;
  customMapStyle?: any;
  testid?: string;
  defaultZoom?: number;
  onRegionChangeComplete: (regionProps: WebAndMobileRegion) => void;
  region?: Region;
  initialRegion: Region;
  mapStyle?: CSSProperties;
  zonesLoading?: boolean;
};

const MapView = (props: WebViewGoogleMap) => {
  const {
    initialRegion,
    region,
    defaultZoom = 16,
    children,
    mapStyle,
    zonesLoading = false,
  } = props || {};
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: config.googleMapsApiKey,
  });

  const [mapRef, setMapRef] = useState<any>(null);
  const { t } = useTranslation();
  const isDesktop = useIsDesktop();
  const tabHeight = useBottomMenuHeight();
  const [center] = useState({
    lat: initialRegion.latitude,
    lng: initialRegion.longitude,
  });

  const currentMapCoordinates = useMapStore((state) => state.mapCoordinates);
  const coordinates = useMapStore((state) => state.userLocation);
  const { height, width } = Dimensions.get('window');
  const [zoom, setZoom] = useState(
    defaultZoom ||
      (region?.latitudeDelta
        ? Math.round(Math.log(360 / region.latitudeDelta) / Math.LN2)
        : initialRegion?.latitudeDelta
          ? Math.round(Math.log(360 / initialRegion.latitudeDelta) / Math.LN2)
          : 15)
  );

  const ASPECT_RATIO = width / height;

  const onUnmount = React.useCallback(function callback() {
    setMapRef(null);
  }, []);

  const onDragEnd = () => {
    const { onRegionChangeComplete } = props;

    if (mapRef && onRegionChangeComplete) {
      const newCenter = mapRef?.getCenter();
      const latDelta =
        mapRef.getBounds().getNorthEast().lat() -
        mapRef.getBounds().getSouthWest().lat();
      onRegionChangeComplete({
        latitude: newCenter.lat(),
        longitude: newCenter.lng(),
        latitudeDelta: latDelta,
        longitudeDelta: latDelta * ASPECT_RATIO,
        zoom: mapRef.getZoom(),
      });
    }
  };
  const onRegionChange = () => false;

  const handleRegionChange = React.useCallback(() => {
    if (mapRef) {
      if (!Array.isArray(currentMapCoordinates)) {
        const { latitude: lat, longitude: lng } = currentMapCoordinates;
        mapRef.panTo({ lat, lng });
        setZoom(17);
        return;
      }
    }
  }, [currentMapCoordinates, mapRef]);

  useEffect(() => {
    if (
      Array.isArray(currentMapCoordinates) ||
      (currentMapCoordinates?.latitude !== 0 &&
        currentMapCoordinates?.longitude !== 0)
    ) {
      handleRegionChange();
    }
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return () => {};
  }, [currentMapCoordinates, handleRegionChange, isLoaded]);

  if (!isLoaded) {
    return <></>;
  }
  return (
    <GoogleMap
      center={center}
      zoom={zoom}
      onZoomChanged={() => {
        setZoom(mapRef?.getZoom() || 16);
      }}
      onLoad={(map) => setMapRef(map)}
      onUnmount={onUnmount}
      mapContainerStyle={
        mapStyle ?? {
          height: '100vh',
          width: '100vw',
          marginTop: !isDesktop ? tabHeight : 0,
        }
      }
      options={{
        fullscreenControl: false,
        mapTypeControl: false,
        maxZoom: 33,
        minZoom: 8,
        streetViewControl: false,
        styles: mapStyles,
        zoomControlOptions: { position: !isDesktop ? 7 : undefined },
      }}
      onDragStart={onRegionChange}
      onIdle={onDragEnd}
      defaultZoom={zoom}
      zonesLoading={zonesLoading}
      aria-label={t('mapViewArialHelp')}
      {...props}>
      {coordinates && (
        <Marker
          position={{ lat: coordinates.latitude, lng: coordinates.longitude }}
          icon={locationIcon}
        />
      )}
      {children}
    </GoogleMap>
  );
};

export default MapView;
